import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import payrollService from '../../services/payrollService';
import companySettingsService from '../../services/companySettingsService';
import { AlertCircle, Download, Printer, Calendar, Building2, User, Phone, Mail } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function SalarySlipGenerator() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const slipRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [salarySlip, setSalarySlip] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [payPeriodStart, setPayPeriodStart] = useState('');
  const [payPeriodEnd, setPayPeriodEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    loadEmployees();
    loadCompanySettings();
    
    // Get params from URL if available
    const employeeId = searchParams?.get('employeeId');
    const start = searchParams?.get('start');
    const end = searchParams?.get('end');

    if (start && end) {
      setPayPeriodStart(start);
      setPayPeriodEnd(end);
    } else {
      // Set default pay period (current month)
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      setPayPeriodStart(firstDay?.toISOString()?.split('T')?.[0]);
      setPayPeriodEnd(lastDay?.toISOString()?.split('T')?.[0]);
    }

    if (employeeId) {
      setSelectedEmployee(employeeId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedEmployee && payPeriodStart && payPeriodEnd) {
      loadSalarySlip();
    }
  }, [selectedEmployee, payPeriodStart, payPeriodEnd]);

  const loadEmployees = async () => {
    try {
      setError('');
      const data = await payrollService?.getAllEmployeesWithSalary();
      setEmployees(data);
    } catch (err) {
      setError(err?.message || 'Failed to load employees');
      console.error('Load employees error:', err);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const settings = await companySettingsService?.getSettings();
      setCompanySettings(settings);
    } catch (err) {
      console.error('Load company settings error:', err);
      // Don't show error to user, just use fallback values
    }
  };

  const loadSalarySlip = async () => {
    try {
      setLoading(true);
      setError('');
      
      const employee = employees?.find(e => e?.employeeId === selectedEmployee);
      if (!employee) {
        setError('Employee not found');
        return;
      }

      const slip = await payrollService?.getEmployeeSalarySlip(
        employee?.id,
        payPeriodStart,
        payPeriodEnd
      );

      if (!slip) {
        setError('No salary record found for this period. Please process payroll first.');
        setSalarySlip(null);
      } else {
        setSalarySlip(slip);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load salary slip');
      console.error('Load salary slip error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!salarySlip || !slipRef?.current) return;

    try {
      setGenerating(true);
      
      const canvas = await html2canvas(slipRef?.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas?.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf?.internal?.pageSize?.getWidth();
      const pdfHeight = pdf?.internal?.pageSize?.getHeight();
      const imgWidth = canvas?.width;
      const imgHeight = canvas?.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf?.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf?.save(`salary-slip-${salarySlip?.employeeId}-${payPeriodStart}.pdf`);
    } catch (err) {
      setError('Failed to generate PDF: ' + err?.message);
      console.error('PDF generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMonthYear = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header sidebarCollapsed={sidebarCollapsed} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Salary Slip Generator</h1>
              <p className="text-gray-600 mt-1">
                Generate and download professional salary slips for employees
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800">{error}</p>
                </div>
                <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
                  ×
                </button>
              </div>
            )}

            {/* Selection Controls */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Select Employee
                  </label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose Employee</option>
                    {employees?.map(emp => (
                      <option key={emp?.employeeId} value={emp?.employeeId}>
                        {emp?.employeeId} - {emp?.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Period Start
                  </label>
                  <input
                    type="date"
                    value={payPeriodStart}
                    onChange={(e) => setPayPeriodStart(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Period End
                  </label>
                  <input
                    type="date"
                    value={payPeriodEnd}
                    onChange={(e) => setPayPeriodEnd(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-end gap-2">
                  <Button
                    onClick={generatePDF}
                    disabled={!salarySlip || generating}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {generating ? 'Generating...' : 'Download PDF'}
                  </Button>
                  <Button
                    onClick={handlePrint}
                    disabled={!salarySlip}
                    variant="outline"
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Salary Slip Preview */}
            {loading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-500">Loading salary slip...</p>
              </div>
            ) : !salarySlip ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-500">
                  {selectedEmployee && payPeriodStart && payPeriodEnd
                    ? 'No salary record found for this period'
                    : 'Select employee and pay period to view salary slip'}
                </p>
              </div>
            ) : (
              <div ref={slipRef} className="bg-white rounded-lg shadow-lg border-2 border-gray-300 p-8">
                {/* Company Header - NOW DYNAMIC */}
                <div className="text-center border-b-2 border-gray-800 pb-6 mb-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {companySettings?.companyName || 'Your Company Name'}
                  </h2>
                  <p className="text-gray-600">
                    {companySettings?.legalEntityName || 'Human Resource Management System'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    {companySettings ? (
                      `${companySettings?.streetAddress}, ${companySettings?.city}, ${companySettings?.stateProvince} ${companySettings?.postalCode}`
                    ) : (
                      'Corporate Office, Business District, City'
                    )}
                  </p>
                  {companySettings?.phoneNumber && (
                    <p className="text-sm text-gray-500 mt-1">
                      <Phone className="w-4 h-4 inline mr-1" />
                      {companySettings?.phoneNumber}
                    </p>
                  )}
                  {companySettings?.email && (
                    <p className="text-sm text-gray-500 mt-1">
                      <Mail className="w-4 h-4 inline mr-1" />
                      {companySettings?.email}
                    </p>
                  )}
                </div>

                {/* Slip Title */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">SALARY SLIP</h3>
                  <p className="text-lg text-gray-700">
                    For the month of {getMonthYear(payPeriodStart)}
                  </p>
                </div>

                {/* Employee Details */}
                <div className="grid grid-cols-2 gap-6 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">Employee ID</p>
                      <p className="text-sm font-semibold text-gray-900">{salarySlip?.employeeId}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">Employee Name</p>
                      <p className="text-sm font-semibold text-gray-900">{salarySlip?.fullName}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">Department</p>
                      <p className="text-sm font-semibold text-gray-900">{salarySlip?.department}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">Designation</p>
                      <p className="text-sm font-semibold text-gray-900">{salarySlip?.position}</p>
                    </div>
                  </div>

                  <div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">Date of Joining</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(salarySlip?.dateOfJoining)}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">PF Number</p>
                      <p className="text-sm font-semibold text-gray-900">{salarySlip?.pfNumber || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">ESI Number</p>
                      <p className="text-sm font-semibold text-gray-900">{salarySlip?.esiNumber || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase">Pay Period</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(payPeriodStart)} to {formatDate(payPeriodEnd)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Salary Breakdown */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Earnings */}
                  <div>
                    <div className="bg-green-50 p-3 rounded-t-lg border-b-2 border-green-600">
                      <h4 className="font-bold text-green-900 uppercase">Earnings</h4>
                    </div>
                    <div className="border border-gray-200 rounded-b-lg">
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Basic Salary</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.baseSalary)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Allowances</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.allowances)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Bonuses</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.bonuses)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Incentives</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.incentives)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Overtime Pay ({salarySlip?.overtimeHours}h)</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.overtimePay)}</span>
                      </div>
                      <div className="p-3 bg-green-100 rounded-b-lg flex justify-between">
                        <span className="text-sm font-bold text-green-900">GROSS PAY</span>
                        <span className="text-sm font-bold text-green-900">{formatCurrency(salarySlip?.grossPay)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div>
                    <div className="bg-red-50 p-3 rounded-t-lg border-b-2 border-red-600">
                      <h4 className="font-bold text-red-900 uppercase">Deductions</h4>
                    </div>
                    <div className="border border-gray-200 rounded-b-lg">
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">PF Contribution</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.4)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">ESI Contribution</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.2)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Professional Tax</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.1)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Income Tax (TDS)</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.taxDeductions)}</span>
                      </div>
                      <div className="p-3 border-b border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-700">Other Deductions</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(salarySlip?.deductions * 0.3)}</span>
                      </div>
                      <div className="p-3 bg-red-100 rounded-b-lg flex justify-between">
                        <span className="text-sm font-bold text-red-900">TOTAL DEDUCTIONS</span>
                        <span className="text-sm font-bold text-red-900">
                          {formatCurrency(salarySlip?.deductions + salarySlip?.taxDeductions)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net Pay */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-lg mb-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-lg font-medium opacity-90">NET SALARY</p>
                      <p className="text-sm opacity-75">(Amount payable)</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">{formatCurrency(salarySlip?.netPay)}</p>
                      <p className="text-sm opacity-75">In Words: {convertToWords(salarySlip?.netPay)}</p>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                {(salarySlip?.bankName || salarySlip?.accountNumber) && (
                  <div className="p-4 bg-blue-50 rounded-lg mb-6">
                    <h4 className="font-bold text-blue-900 mb-3">BANK DETAILS</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-600 uppercase">Bank Name</p>
                        <p className="text-sm font-semibold text-gray-900">{salarySlip?.bankName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 uppercase">Account Number</p>
                        <p className="text-sm font-semibold text-gray-900">{salarySlip?.accountNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 uppercase">IFSC Code</p>
                        <p className="text-sm font-semibold text-gray-900">{salarySlip?.ifscCode || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer - Update with company info */}
                <div className="border-t-2 border-gray-300 pt-6 mt-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-xs text-gray-500">Generated on: {formatDate(new Date()?.toISOString())}</p>
                      {companySettings?.registrationNumber && (
                        <p className="text-xs text-gray-500 mt-1">
                          Registration No: {companySettings?.registrationNumber}
                        </p>
                      )}
                      {companySettings?.taxId && (
                        <p className="text-xs text-gray-500 mt-1">
                          GST/Tax ID: {companySettings?.taxId}
                        </p>
                      )}
                      {salarySlip?.payrollNotes && (
                        <p className="text-xs text-gray-600 mt-2 italic">Note: {salarySlip?.payrollNotes}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="border-t-2 border-gray-800 w-48 mb-2"></div>
                      <p className="text-sm font-semibold text-gray-900">Authorized Signature</p>
                      <p className="text-xs text-gray-500">HR Department</p>
                      <p className="text-xs text-gray-500">{companySettings?.companyName || 'Company'}</p>
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="mt-6 p-3 bg-gray-100 rounded text-center">
                  <p className="text-xs text-gray-600">
                    This is a computer-generated salary slip and does not require a physical signature.
                    For any queries, please contact the HR department.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// Helper function to convert number to words (Indian format)
function convertToWords(amount) {
  if (!amount || amount === 0) return 'Zero Rupees Only';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  const convertChunk = (num) => {
    if (num === 0) return '';
    if (num < 10) return ones?.[num];
    if (num < 20) return teens?.[num - 10];
    if (num < 100) return tens?.[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones?.[num % 10] : '');
    return ones?.[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + convertChunk(num % 100) : '');
  };

  const numStr = Math.floor(amount)?.toString();
  let result = '';

  if (numStr?.length > 7) {
    const crores = parseInt(numStr?.slice(0, -7));
    result += convertChunk(crores) + ' Crore ';
  }
  if (numStr?.length > 5) {
    const lakhs = parseInt(numStr?.slice(-7, -5));
    if (lakhs > 0) result += convertChunk(lakhs) + ' Lakh ';
  }
  if (numStr?.length > 3) {
    const thousands = parseInt(numStr?.slice(-5, -3));
    if (thousands > 0) result += convertChunk(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr?.slice(-3));
  if (hundreds > 0) result += convertChunk(hundreds);

  return (result?.trim() + ' Rupees Only')?.trim();
}